import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Logo from "../../Assets/logo.png";
// Icons
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArticleIcon from '@mui/icons-material/Article'; // For sample papers
import AssignmentIcon from '@mui/icons-material/Assignment'; // For final papers
import AssessmentIcon from '@mui/icons-material/Assessment'; // For reports
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GavelIcon from '@mui/icons-material/Gavel'; // For rules
import BugReportIcon from '@mui/icons-material/BugReport';
import BookmarkIcon from '@mui/icons-material/Bookmark'; // For marked questions
import LogoutIcon from '@mui/icons-material/Logout';

const Sidebar = () => {
  const dispatch = useDispatch();
  //const { firstname, lastname } = useSelector((state) => state.auth?.student?.student);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const sidebarRef = useRef(null);
  const hamburgerRef = useRef(null);

  const logoutHandler = () => {
    dispatch({ type: "LOGOUT_STUDENT" });
    toast.success("Logged Out Successfully!");
  };

  const toggleSidebar = () => setIsOpen(!isOpen);

  // Close sidebar when clicking outside (works on all devices)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      {/* Hamburger Button (shown on ALL devices) */}
      <button
        ref={hamburgerRef}
        onClick={toggleSidebar}
        className="fixed top-3 left-0 z-50 p-2 rounded-md text-gray-400 hover:bg-gray-100"
      >
        {isOpen ? <CloseIcon /> : <MenuIcon />}
      </button>

      {/* Sidebar (can be toggled on ALL devices) */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full mt-16 md:mt-0 z-40 w-64 bg-white shadow-lg border-r border-gray-200 transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out`}
      >
        {/* Logo Section */}
        <div className="flex flex-col items-center py-4 px-4 border-b border-gray-200">
          <Link to="/" className="mb-2" onClick={() => setIsOpen(false)}>
            <img src={Logo} alt="Logo" className="h-10 w-auto" />
          </Link>
          <h5 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Student Panel</h5>
        </div>

        {/* Navigation Links with Scroll */}
        <div className="flex flex-col h-[calc(100%-120px)]">
          <div className="overflow-y-auto flex-1 py-2">
            <ul className="space-y-1 px-2">
              <Link to="/" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <ArticleIcon className="text-current mr-3" />
                  <span className="font-medium">Sample Papers</span>
                </li>
              </Link>

              <Link to="/final-papers" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/final-papers" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <AssignmentIcon className="text-current mr-3" />
                  <span className="font-medium">Final Papers</span>
                </li>
              </Link>

              <Link to="/records" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/records" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <AssessmentIcon className="text-current mr-3" />
                  <span className="font-medium">Reports</span>
                </li>
              </Link>

              <Link to="/profile" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/profile" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <AccountCircleIcon className="text-current mr-3" />
                  <span className="font-medium">Profile</span>
                </li>
              </Link>

              <Link to="/rules" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/rules" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <GavelIcon className="text-current mr-3" />
                  <span className="font-medium">Rules</span>
                </li>
              </Link>
              <Link to="/notice-board" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/notice-board" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <GavelIcon className="text-current mr-3" />
                  <span className="font-medium">Notice Board</span>
                </li>
              </Link>

              <Link to="/report-issue" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/report-issue" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <BugReportIcon className="text-current mr-3" />
                  <span className="font-medium">Report An Issue</span>
                </li>
              </Link>

              <Link to="/marked/questions" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/marked/questions" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <BookmarkIcon className="text-current mr-3" />
                  <span className="font-medium">Marked Questions</span>
                </li>
              </Link>
              {/* <Link to="/chat" className="block" onClick={() => setIsOpen(false)}>
                <li className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === "/marked/questions" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"
                }`}>
                  <BookmarkIcon className="text-current mr-3" />
                  <span className="font-medium">Message</span>
                </li>
              </Link> */}
            </ul>
          </div>

          {/* Logout Button (fixed at bottom) */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={logoutHandler}
              className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <LogoutIcon className="w-5 h-5 mr-1" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;